import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import apiClient, { endpoints } from '../api';
import './ResetPassword.css';

const ResetPassword = ({ onBackToLogin }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      setError('Invalid or missing reset token. Please request a new password reset.');
      return;
    }
    
    setToken(tokenParam);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    if (!formData.newPassword) {
      setError('New password is required');
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (!formData.confirmPassword) {
      setError('Please confirm your password');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!token) {
      setError('Invalid reset token. Please request a new password reset.');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await apiClient.post(endpoints.resetPassword, {
        token: token,
        newPassword: formData.newPassword
      });

      if (response.status === 200) {
        setMessage('Your password has been successfully reset. Please log in again.');
        // Redirect to login after 3 seconds
        setTimeout(() => {
          onBackToLogin();
        }, 3000);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      
      if (error.response) {
        const errorData = error.response.data;
        if (errorData.error === 'Invalid or expired token') {
          setError('This link is invalid or expired. Please request a new password reset.');
        } else {
          setError(errorData.error || 'Failed to reset password. Please try again.');
        }
      } else if (error.request) {
        setError('No response from server. Please check if backend is running.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleBackToLogin = () => {
    onBackToLogin();
  };

  if (!token && !error) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Validating reset token...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <div className="reset-password-logo">
            <h1>SonicKart</h1>
            <p style={{color:"#ffc727"}}>From your store to every doorstep with SonicKart</p>
            <h2 style={{color:"#ffffff"}}>Set New Password</h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          {error && (
            <div className="error-message general">
              <LucideIcons.AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="success-message general">
              <LucideIcons.CheckCircle size={16} />
              <span>{message}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <div className="input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter new password"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('password')}
                disabled={isLoading}
              >
                {showPassword ? <LucideIcons.EyeOff size={20} /> : <LucideIcons.Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <div className="input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="Confirm new password"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
                disabled={isLoading}
              >
                {showConfirmPassword ? <LucideIcons.EyeOff size={20} /> : <LucideIcons.Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`reset-password-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="reset-password-footer">
          <p>
            Remember your password? 
            <button 
              type="button" 
              className="switch-link"
              onClick={handleBackToLogin}
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
